import { BadgeCheck } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import dayjs from 'dayjs';

import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

import { type IAssetReport } from '../model';

const chartConfig = {
  commits: {
    label: 'Commits',
    color: 'var(--color-secondary-foreground)',
  },
} satisfies ChartConfig;

const CommitsChart = ({
  commitsPerDay = {},
  period,
}: {
  commitsPerDay: NonNullable<IAssetReport>['data']['commitsPerDay'];
  period: string;
}) => {
  const totalDays = dayjs(period).daysInMonth();
  const commitsPerDayMap: Record<number, number> = Object.entries(commitsPerDay ?? {}).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [dayjs(key).date() - 1]: value,
    };
  }, {});
  const commits = Array.from({ length: totalDays }, (_, i) => ({
    day: i + 1,
    commits: commitsPerDayMap[i] || 0,
  }));

  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <BarChart
        accessibilityLayer
        data={commits}
        margin={{
          top: 20,
          left: -30,
          right: 0,
        }}
        barSize={8}
      >
        <CartesianGrid vertical={false} />
        <XAxis dataKey="day" tickLine={false} tickMargin={10} axisLine={false} fontSize={10} interval={0} />
        <YAxis dataKey="commits" tickLine={false} tickMargin={10} axisLine={false} fontSize={10} />
        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
        <Bar dataKey="commits" fill="var(--color-secondary-foreground)" radius={8}>
          <LabelList position="top" offset={2} className="fill-primary-foreground" fontSize={8} fontWeight={700} />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
};

type Props = {
  assetReport: IAssetReport;
};

const ActivityVerification = ({ assetReport }: Props) => {
  return (
    <div>
      <div className="flex items-center justify-start gap-2.25 mb-3">
        <h2 className="text-sm text-foreground">Activity - 1 Month</h2>
        <Badge className="text-[#166534] bg-[#DCFCE7] gap-1 text-[10px]">
          <BadgeCheck className="size-2.75" />
          {assetReport?.data.totalCommits ? 'Development activity' : 'No activity'}
        </Badge>
      </div>
      <div className="bg-[rgba(244,246,247,0.60)] rounded p-3.5 space-y-2 max-w-[158px]">
        <div className="text-[10px] text-foreground opacity-80">
          Total commits in {dayjs(assetReport?.period).format('MMMM')}
        </div>
        <div className="text-foreground text-xl font-bold">{assetReport?.data.totalCommits ?? 0}</div>
      </div>
      <Separator className="my-6" />
      <div className="space-y-1.5 mb-3">
        <h2 className="text-sm text-foreground">{assetReport?.asset.depositSource}</h2>
        <div className="text-muted-foreground text-[11px] font-semibold">Commits per day</div>
      </div>
      <CommitsChart commitsPerDay={assetReport?.data.commitsPerDay} period={assetReport?.period ?? ''} />
    </div>
  );
};

export default ActivityVerification;
